import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { FeatureFlag } from '../types';
import { SystemEntity } from '@backstage/catalog-model';
import { NewFeatureFlagCard } from './NewFeatureFlagCard';
import { Alert } from '@material-ui/lab';

export const FeaturesListingCard = (props: {
  system: SystemEntity;
  selectedFlag: FeatureFlag | null;
  setSelectedFlag: (flag: FeatureFlag) => void;
  featureFlags: FeatureFlag[];
  setFeatureFlags: (flags: FeatureFlag[]) => void;
}) => {
  const {
    system,
    selectedFlag,
    setSelectedFlag,
    featureFlags,
    setFeatureFlags,
  } = props;
  const theme = useTheme();

  const [newFlagModalOpen, setNewFlagModalOpen] = useState(false);
  const [filteredFlags, setFilteredFlags] =
    useState<FeatureFlag[]>(featureFlags);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorAlertMsg, setErrorAlertMsg] = useState('');

  useEffect(() => {
    setFilteredFlags(featureFlags);
  }, [featureFlags]);

  const handleAddFeatureFlag = (newFlag: FeatureFlag) => {
    const existingFlag = featureFlags.find(flag => flag.code === newFlag.code);
    if (existingFlag) {
      setOpenErrorAlert(true);
      setErrorAlertMsg(
        `Feature flag with code '${newFlag.code}' already exists.`,
      );
      setNewFlagModalOpen(false);
      return;
    }
    setFeatureFlags([...featureFlags, newFlag]);
    setNewFlagModalOpen(false);
  };

  const borderBottomColor = useCallback(
    (flag: FeatureFlag) => {
      if (flag?.wasCreated) return `4px solid ${theme.palette.warning.main}`;
      if (!selectedFlag) return 'none';

      return selectedFlag.code === flag.code
        ? `4px solid ${theme.palette.secondary.main}`
        : 'none';
    },
    [selectedFlag, theme.palette.secondary.main, theme.palette.warning.main],
  );

  const flagLabel = (flag: FeatureFlag) => {
    if (flag?.wasCreated) return 'SUBMIT FIRST';
    return flag.state.toUpperCase() === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE';
  };

  const flagLabelColor = (flag: FeatureFlag) => {
    if (flag?.wasCreated) return theme.palette.warning.main;
    return flag.state.toUpperCase() === 'ACTIVE'
      ? theme.palette.success.main
      : theme.palette.error.main;
  };

  return (
    <>
      <Box
        style={{
          width: 400,
          overflow: 'auto',
          scrollbarWidth: 'thin',
          paddingRight: 8,
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          gridColumnGap={8}
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          mt={1}
        >
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            color="secondary"
            fullWidth
            onChange={e =>
              setFilteredFlags(
                featureFlags.filter(flag =>
                  flag.code
                    .toLowerCase()
                    .includes(e.target.value.toLowerCase()),
                ),
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setNewFlagModalOpen(true)}
          >
            New
          </Button>
        </Box>
        {filteredFlags.map(flag => (
          <Card
            key={flag.code}
            style={{
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[2],
              marginBottom: 16,
              borderBottom: borderBottomColor(flag),
            }}
            onClick={() => setSelectedFlag(flag)}
          >
            <CardContent style={{ cursor: 'pointer' }}>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="h6"
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {flag.code}
                </Typography>
                <Box display="flex" alignItems="center" ml={1}>
                  <Chip
                    label={flagLabel(flag)}
                    style={{
                      backgroundColor: flagLabelColor(flag),
                      color: '#fff',
                      fontWeight: 'bold',
                      margin: 0,
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {newFlagModalOpen && (
        <Dialog
          open={newFlagModalOpen}
          onClose={() => setNewFlagModalOpen(false)}
        >
          <DialogTitle>Add feature flag</DialogTitle>
          <DialogContent>
            <NewFeatureFlagCard
              system={system}
              onCreate={handleAddFeatureFlag}
            />
          </DialogContent>
        </Dialog>
      )}

      <Snackbar
        open={openErrorAlert}
        onClose={() => setOpenErrorAlert(false)}
        autoHideDuration={5000}
      >
        <Alert onClose={() => setOpenErrorAlert(false)} severity="error">
          {errorAlertMsg}
        </Alert>
      </Snackbar>
    </>
  );
};
